import React from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { SelectList } from "@common/selectList";
import { Calendar } from "@common/calendar";
import { Props } from "./types";
import styles from "./styles.scss";

const times = Object.fromEntries(
  Array.from({ length: 96 }, (_, i) => {
    const hours = Math.floor(i / 4);
    const minutes = (i % 4) * 15;
    return [`${hours}h${String(minutes).padStart(2, "0")}`, { hours, minutes }];
  }),
);

function DateTimeInput({
  dateTime,
  projectTimezone,
  placeholder,
  isInvalid,
  input,
}: Props) {
  const { t } = useTranslation();
  // Conversion UTC → fuseau projet pour l'affichage
  const display = dateTime
    ? DateTime.fromISO(dateTime, { zone: "utc" }).setZone(projectTimezone)
    : DateTime.now().setZone(projectTimezone);

  const isValid = display.isValid;

  const [timeText, setTimeText] = React.useState(
    dateTime && isValid ? display.toFormat("HH:mm") : "00:00",
  );

  React.useEffect(() => {
    const next = dateTime
      ? DateTime.fromISO(dateTime, { zone: "utc" }).setZone(projectTimezone)
      : null;
    setTimeText(next?.isValid ? next.toFormat("HH:mm") : "00:00");
  }, [dateTime, projectTimezone]);

  // Construction d'un DateTime en fuseau projet → toUTC().toISO()
  const commitDateTime = (
    year: number,
    month: number,
    day: number,
    hour: number,
    minute: number,
  ) => {
    const next = DateTime.fromObject(
      { year, month, day, hour, minute, second: 0, millisecond: 0 },
      { zone: projectTimezone },
    );
    if (!next.isValid) return;
    input(next.toUTC().toISO());
  };

  const commitTime = (text: string) => {
    const match = text.match(/^(\d{1,2}):(\d{2})$/);
    const h = match ? parseInt(match[1], 10) : NaN;
    const m = match ? parseInt(match[2], 10) : NaN;
    if (!match || h > 23 || m > 59) {
      setTimeText(dateTime && isValid ? display.toFormat("HH:mm") : "00:00");
      return;
    }
    const base = dateTime ? display : DateTime.now().setZone(projectTimezone);
    commitDateTime(base.year, base.month, base.day, h, m);
  };

  return (
    <div
      className={classNames(styles.dateTimeInput, {
        [styles.invalid]: isInvalid,
      })}
      title={t("dateTimeInput.projectTimezoneTitle", {
        timezone: projectTimezone,
      })}
    >
      <SelectList<string>
        rootClassName={styles.calendarInput}
        listClassName={styles.calendar}
      >
        {({ select }) => ({
          input: (
            <div className={styles.input}>
              {dateTime && isValid ? (
                <p className={styles.value}>{display.toFormat("dd/MM/yyyy")}</p>
              ) : (
                <p className={styles.placeholder}>{placeholder}</p>
              )}
            </div>
          ),
          list: (
            <Calendar
              date={{
                year: display.year,
                month: display.month - 1, // Luxon: 1-12, Calendar attend 0-11
                day: display.day,
              }}
              editDate={({ year, month, day }) => {
                const base = dateTime
                  ? display
                  : DateTime.now().setZone(projectTimezone);
                commitDateTime(year, month + 1, day, base.hour, base.minute);
                select(""); // ou la valeur appropriée selon ton API SelectList
              }}
            />
          ),
        })}
      </SelectList>

      <SelectList<string> listClassName={styles.timeListWrapper}>
        {({ select }) => ({
          input: (
            <div className={styles.input}>
              <input
                type="text"
                className={classNames(
                  styles.timeInput,
                  dateTime ? styles.value : styles.placeholder,
                )}
                value={timeText}
                onChange={(e) => setTimeText(e.target.value)}
                onBlur={() => commitTime(timeText)}
                onKeyDown={(e) => e.key === "Enter" && commitTime(timeText)}
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
            </div>
          ),
          list: (
            <ul className={styles.timeList}>
              {Object.keys(times).map((key) => {
                const time = times[key as keyof typeof times];
                return (
                  <li
                    key={key}
                    className={styles.time}
                    onClick={() => {
                      const base = dateTime
                        ? display
                        : DateTime.now().setZone(projectTimezone);
                      commitDateTime(
                        base.year,
                        base.month,
                        base.day,
                        time.hours,
                        time.minutes,
                      );
                      select("");
                    }}
                  >
                    <p className={styles.timeName}>{key}</p>
                  </li>
                );
              })}
            </ul>
          ),
        })}
      </SelectList>
    </div>
  );
}

export { DateTimeInput };
